import type { EmployeeSchema, EmployeeSchemaWithId } from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Employee } from "~/providers/employee-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { useEmployeeStore } from "~/stores/use-employee-store";
import { usePermissionManager } from "./use-permission-manager";

export const useEmployeeManager = () => {
  const { t } = useTranslation("common");

  const { setEmployeeDialogOpen, setSelectedEmployee, setProfileOpen } =
    useEmployeeStore();

  const { handleEmployeeUpdate } = useEmployeeUpdates();

  const { showDialog } = useAlertStore();

  const { hasPermission } = usePermissionManager();

  const { mutate: create } = api.employee.create.useMutation({
    async onSuccess(result) {
      toast.success(
        capitalize(t("create_item_success", { item: t("Employee.employee") })),
      );
      await handleEmployeeUpdate(result);
    },
    onError() {
      toast.error(
        capitalize(t("create_item_error", { item: t("Employee.employee") })),
      );
    },
  });

  const { mutate: update } = api.employee.update.useMutation({
    async onSuccess(_, variables) {
      toast.success(
        capitalize(t("update_item_success", { item: t("Employee.employee") })),
      );
      await handleEmployeeUpdate(variables.id);
    },
    onError() {
      toast.error(
        capitalize(t("update_item_error", { item: t("Employee.employee") })),
      );
    },
  });

  const { mutate: deleteEmployee } = api.employee.deleteEmployee.useMutation({
    onSuccess() {
      toast.success(t("delete_item_success", { item: t("Employee.employee") }));
    },
  });

  const createEmployee = async (
    data: z.infer<ReturnType<typeof EmployeeSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_employee,
      label: t("Permissions.create_employee_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof EmployeeSchema>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });

      create(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError(error) {
          if (error.data?.code === "CONFLICT") {
            return resolve({
              error: {
                path: "pin",
                message: capitalize(t("Employee.employee_with_pin_exists")),
              },
            });
          }
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("create_item_error", {
                  item: t("Employee.employee"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const updateEmployee = async (
    data: z.infer<ReturnType<typeof EmployeeSchemaWithId>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.update_employee_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof EmployeeSchemaWithId>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });

      update(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError() {
          resolve({
            error: {
              path: "root",
              message: capitalize(
                t("update_item_error", { item: t("Employee.employee") }),
              ),
            },
          });
        },
      });
    });
  };

  const deleteEmployeeHandler = async (employee: Employee) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.delete_employee,
    });

    const result = await showDialog({
      title: t("Employee.before_delete_title", {
        first_name: employee.firstName,
      }),
      message: t("Employee.before_delete_message"),
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    if (result === "cancel") return false;

    return new Promise<boolean>((resolve) => {
      if (!permission) {
        resolve(false);
      }

      deleteEmployee(employee.id, {
        onSuccess: () => {
          resolve(true);
        },
      });
    });
  };

  const openEmployeeDialog = (employee?: Employee) => {
    setSelectedEmployee(employee);
    setEmployeeDialogOpen(true);
  };

  const openProfile = () => {
    setProfileOpen(true);
  };

  return {
    deleteEmployee: deleteEmployeeHandler,
    openEmployeeDialog,
    updateEmployee,
    createEmployee,
    openProfile,
  };
};

export const useEmployeeUpdates = () => {
  const utils = api.useUtils();

  const handleEmployeeUpdate = useCallback(
    async (employeeId: string) => {
      const result = await utils.employee.getById.fetch(employeeId);

      void utils.employee.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [utils.employee.getAll, utils.employee.getById],
  );

  const handleEmployeeDelete = useCallback(
    (employeeId: string) => {
      void utils.employee.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== employeeId);
      });
    },
    [utils.employee.getAll],
  );

  return {
    handleEmployeeUpdate,
    handleEmployeeDelete,
  };
};
